import { jsx } from "react/jsx-runtime";
import { createContext, useContext, useLayoutEffect, useMemo, useRef } from "react";
import { createStore } from "zustand";
import { useTheme } from "../useTheme.js";
import { FALLBACK_BREAKPOINT, getFallbackMatchMedia, getSupportsMatchMedia } from "./fallbackMatchMediaUtils.js";
import { getFallbackBreakpointsStore, isMobileBreakpoint, setActiveBreakpoint as external_breakpoint_provider_model_js_setActiveBreakpoint } from "./breakpoint-provider-model.js";
const REPLACE_MEDIA_REGEX = /^@media( ?)/m;
function getBreakpointToMediaQueryMap(breakpoints) {
    return breakpoints.keys.reduce((result, breakpointKey, index)=>{
        let mediaQuery;
        if (index < breakpoints.keys.length - 1) {
            const nextBreakpointKey = breakpoints.keys[index + 1];
            mediaQuery = breakpoints.between(breakpointKey, nextBreakpointKey);
        } else mediaQuery = breakpoints.up(breakpointKey);
        return {
            ...result,
            [breakpointKey]: mediaQuery.replace(REPLACE_MEDIA_REGEX, '')
        };
    }, {});
}
function getBreakpointToMediaQueryListMap(breakpoints) {
    const supportsMatchMedia = getSupportsMatchMedia();
    const breakpointToMediaQueryMap = getBreakpointToMediaQueryMap(breakpoints);
    return breakpoints.keys.reduce((result, breakpointKey)=>({
            ...result,
            [breakpointKey]: supportsMatchMedia ? window.matchMedia(breakpointToMediaQueryMap[breakpointKey]) : getFallbackMatchMedia(breakpointKey === FALLBACK_BREAKPOINT)
        }), {});
}
function initializeBreakpointStore(breakpoints, breakpointToMediaQueryMap) {
    return createStore()((set)=>{
        let activeBreakpoint = FALLBACK_BREAKPOINT;
        const breakpointMatches = breakpoints.keys.reduce((result, breakpointKey)=>{
            if (breakpointToMediaQueryMap[breakpointKey].matches) activeBreakpoint = breakpointKey;
            return {
                ...result,
                [breakpointKey]: breakpointToMediaQueryMap[breakpointKey].matches
            };
        }, {});
        return {
            isLoading: false,
            ...breakpointMatches,
            activeBreakpoint,
            isMobile: isMobileBreakpoint(activeBreakpoint),
            setActiveBreakpoint: (breakpoint)=>set(()=>external_breakpoint_provider_model_js_setActiveBreakpoint(breakpoints, breakpoint))
        };
    });
}
const BreakpointsContext = /*#__PURE__*/ createContext(void 0);
function ViewportBreakpointsProvider({ children }) {
    const { theme: { breakpoints } } = useTheme();
    const breakpointToMediaQueryMap = useMemo(()=>getBreakpointToMediaQueryListMap(breakpoints), [
        breakpoints
    ]);
    const storeRef = useRef();
    if (!storeRef.current) storeRef.current = initializeBreakpointStore(breakpoints, breakpointToMediaQueryMap);
    useLayoutEffect(()=>{
        if (!storeRef.current) throw new Error('Something went wrong initializing the ViewportBreakpointsProvider.');
        const setActiveBreakpoint = storeRef.current.getState().setActiveBreakpoint;
        const callbackFns = breakpoints.keys.map((breakpointKey)=>{
            const callback = (e)=>{
                if (e.matches) setActiveBreakpoint(breakpointKey);
            };
            breakpointToMediaQueryMap[breakpointKey].addEventListener('change', callback);
            return callback;
        });
        return ()=>{
            breakpoints.keys.map((breakpointKey, i)=>{
                breakpointToMediaQueryMap[breakpointKey].removeEventListener('change', callbackFns[i]);
            });
        };
    }, [
        breakpointToMediaQueryMap,
        breakpoints.keys
    ]);
    return /*#__PURE__*/ jsx(BreakpointsContext.Provider, {
        value: storeRef.current,
        children: children
    });
}
const useViewportBreakpointsStore = ()=>{
    const store = useContext(BreakpointsContext);
    if (!store) if ('test' === process.env.NODE_ENV) return getFallbackBreakpointsStore();
    else throw new Error('Missing ViewportBreakpointsProvider');
    return store;
};
export { ViewportBreakpointsProvider, getBreakpointToMediaQueryListMap, getBreakpointToMediaQueryMap, useViewportBreakpointsStore };

//# sourceMappingURL=ViewportBreakpointsProvider.js.map